/*
 * Copyright, AlpineReplay, Inc. 2021. All rights reserved.
 * Author: Brian Bal, Slobodan Djordjevic
 */
import { multisportify } from "trux"
import { webApi } from "../../lib/WebApi"
import { calculateBaseballStats } from "../../util/helpers"

let defaultValue = {
    loading: false,
    complete: false,
    error: null,
    value: null,
}

const getStatRow = (tn) => {
    return {
        trace_number: tn,
        accel_rank: 0,
        acceleration: 0,
        distance: 0,
        distance_rank: 0,
        effort: 0,
        effort_rank: 0,
        minutes_played: 0,
        minutes_rank: 0,
        speed_rank: 0,
        stamina: 0,
        stamina_rank: 0,
        top_speed: 0,
    }
}

const defaultStats = {
    first_half: [
        getStatRow("1"),
        getStatRow("2"),
        getStatRow("3"),
        getStatRow("4"),
        getStatRow("5"),
        getStatRow("6"),
        getStatRow("7"),
        getStatRow("8"),
        getStatRow("9"),
        getStatRow("10"),
        getStatRow("11"),
        getStatRow("12"),
        getStatRow("13"),
        getStatRow("14"),
        getStatRow("15"),
        getStatRow("16"),
        getStatRow("17"),
        getStatRow("18"),
        getStatRow("19"),
        getStatRow("20"),
    ],
    second_half: [
        getStatRow("1"),
        getStatRow("2"),
        getStatRow("3"),
        getStatRow("4"),
        getStatRow("5"),
        getStatRow("6"),
        getStatRow("7"),
        getStatRow("8"),
        getStatRow("9"),
        getStatRow("10"),
        getStatRow("11"),
        getStatRow("12"),
        getStatRow("13"),
        getStatRow("14"),
        getStatRow("15"),
        getStatRow("16"),
        getStatRow("17"),
        getStatRow("18"),
        getStatRow("19"),
        getStatRow("20"),
    ],
    game: [
        getStatRow("1"),
        getStatRow("2"),
        getStatRow("3"),
        getStatRow("4"),
        getStatRow("5"),
        getStatRow("6"),
        getStatRow("7"),
        getStatRow("8"),
        getStatRow("9"),
        getStatRow("10"),
        getStatRow("11"),
        getStatRow("12"),
        getStatRow("13"),
        getStatRow("14"),
        getStatRow("15"),
        getStatRow("16"),
        getStatRow("17"),
        getStatRow("18"),
        getStatRow("19"),
        getStatRow("20"),
    ],
}

const loadPlayerStats = (gameId) => {
    return (dispatch, getState) => {
        let playerStatsState = getState().player_stats
        let playerStatsBaseballState = getState().player_stats_baseball
        let gameState = getState().game
        if (
            !gameState.value ||
            gameState.complete !== true ||
            // eslint-disable-next-line eqeqeq
            gameState.error != null
        ) {
            return { type: "NOOP" }
        }

        if (
            (playerStatsState.complete &&
                playerStatsState.value &&
                gameId === playerStatsState.value.game_id) ||
            playerStatsState.loading
        ) {
            return { type: "NOOP" }
        }

        if (
            gameState.value &&
            gameState.value.sport_type === "baseball" &&
            (playerStatsBaseballState.complete ||
                playerStatsBaseballState.loading)
        ) {
            return { type: "NOOP" }
        }

        let gameIdInt = -1
        try {
            gameIdInt = parseInt(gameId)
        } catch (e) {
            gameIdInt = -1
        }

        if (gameIdInt <= 0 || gameIdInt !== gameIdInt) {
            return { type: "NOOP" }
        }

        let sportType = gameState.value.sport_type

        let players = gameState.value.home_team.players

        if (sportType === "soccer") {
            // Start player stats loading
            dispatch({
                type: "COMMON_SET",
                key: "player_stats",
                value: { loading: true, complete: false, error: null },
                defaultValue: { ...defaultValue },
            })

            fetchPlayerStats(gameId)
                .then((stats) => {
                    if (!stats) {
                        stats = { ...defaultStats }
                    }
                    stats = multisportify(stats)
                    stats["game_id"] = gameId
                    if (stats.first_half) {
                        stats.first_half = fixupStats(stats.first_half, players)
                        stats.second_half = fixupStats(
                            stats.second_half,
                            players
                        )
                        stats.game = fixupStats(stats.game, players)
                        stats.top_first_half = buildLeaders(stats.first_half)
                        stats.top_second_half = buildLeaders(stats.second_half)
                        stats.top_game = buildLeaders(stats.game)
                    } else {
                        stats.first_half = []
                        stats.second_half = []
                        stats.game = []
                    }

                    console.log("loadPlayerStats", stats)
                    dispatch({
                        type: "COMMON_SET",
                        key: "player_stats",
                        value: {
                            value: stats,
                            loading: false,
                            complete: true,
                            error: null,
                        },
                        defaultValue: { ...defaultValue },
                    })
                })
                .catch((e) => {
                    console.error("loadPlayerStats ERROR", e)
                    dispatch({
                        type: "COMMON_SET",
                        key: "player_stats",
                        value: {
                            value: { ...defaultStats },
                            loading: false,
                            complete: true,
                            error: e,
                        },
                        defaultValue: { ...defaultValue },
                    })
                })
        } else if (sportType === "baseball") {
            let stats = []
            let game = gameState.value
            if (game) {
                dispatch({
                    type: "COMMON_SET",
                    key: "player_stats_baseball",
                    value: { loading: true, complete: true, error: null },
                    defaultValue: { ...defaultValue },
                })
                stats = calculateBaseballStats(game)
            }
            dispatch({
                type: "COMMON_SET",
                key: "player_stats_baseball",
                value: {
                    value: stats,
                    loading: false,
                    complete: true,
                    error: null,
                },
                defaultValue: { ...defaultValue },
            })
        }
    }
}

function fixupStats(stats, players) {
    if (!stats) {
        stats = []
    }
    for (let stat of stats) {
        let found = false
        for (let player of players) {
            if (stat.trace_number === player.trace_number) {
                stat.athlete = player
                break
            }
        }
        if (!found) {
            stat.unknown = true
        }

        stat["effort_formatted"] = stat.effort.toFixed(0)
        stat["stamina_formatted"] = stat.stamina.toFixed(0) + "%"
        stat["distance_formatted"] = stat.distance.toFixed(1)
        stat["top_speed_formatted"] = stat.top_speed.toFixed(1)
        stat["acceleration_formatted"] = stat.acceleration.toFixed(1)
        stat["minutes_formatted"] = !stat.minutes_played
            ? "--"
            : stat.minutes_played.toFixed(0)
    }

    stats = addRankingsToPlayerStats(stats, "effort", false)
    stats = addRankingsToPlayerStats(stats, "stamina", false)
    stats = addRankingsToPlayerStats(stats, "distance", false)
    stats = addRankingsToPlayerStats(stats, "top_speed", false)
    stats = addRankingsToPlayerStats(stats, "acceleration", false)

    return stats
}

function buildLeaders(stats) {
    let efforstTop = getTopPlayerStat("effort", false, stats)
    let energyTop = getTopPlayerStat("stamina", false, stats)
    let distanceTop = getTopPlayerStat("distance", false, stats)
    let speedTop = getTopPlayerStat("top_speed", false, stats)
    let accelTop = getTopPlayerStat("acceleration", true, stats)
    return {
        effort: {
            athlete: efforstTop?.athlete,
            value: efforstTop?.effort,
            formatted_value: efforstTop?.effort_formatted,
        },
        stamina: {
            athlete: energyTop?.athlete,
            value: energyTop?.stamina,
            formatted_value: energyTop?.stamina_formatted,
        },
        distance: {
            athlete: distanceTop?.athlete,
            value: distanceTop?.distance,
            formatted_value: distanceTop?.distance_formatted,
        },
        top_speed: {
            athlete: speedTop?.athlete,
            value: speedTop?.top_speed,
            formatted_value: speedTop?.top_speed_formatted,
        },
        acceleration: {
            athlete: accelTop?.athlete,
            value: accelTop?.acceleration,
            formatted_value: accelTop?.acceleration_formatted,
        },
    }
}

function getTopPlayerStat(key, asc, stats) {
    // Filter
    if (!stats) {
        return []
    }

    var hiVal = -9999999
    var loVal = 9999999
    var hiIndex = -1
    var loIndex = -1
    for (let i = 0; i < stats.length; i++) {
        if (stats[i][key] > hiVal) {
            hiVal = stats[i][key]
            hiIndex = i
        }
        if (stats[i][key] < loVal) {
            loVal = stats[i][key]
            loIndex = i
        }
    }

    if (loIndex < 0) {
        loIndex = 1
    }
    if (hiIndex < 0) {
        hiIndex = 1
    }

    if (asc) {
        return stats[loIndex]
    } else {
        return stats[hiIndex]
    }
}

export function addRankingsToPlayerStats(playerStats, key, asc) {
    // sort
    playerStats = playerStats.sort((a, b) => {
        let val = a[key] - b[key]
        if (!asc) {
            val = val * -1
        }
        return val
    })

    for (let i = 0; i < playerStats.length; i++) {
        playerStats[i][key + "_rank"] = i
    }

    return playerStats
}

function fetchPlayerStats(gameId) {
    return new Promise((resolve, reject) => {
        let uri = `/webapp/games/${gameId}/players-stats`
        webApi
            ._get(uri)
            .then((json) => {
                resolve(json)
            })
            .catch((err) => {
                console.error(err)
                resolve(null)
            })
    })
}

export default loadPlayerStats
